import { useQuery } from '@tanstack/react-query';
import { Layers } from 'lucide-react';
import type { Map as MapLibreMap } from 'maplibre-gl';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { canAny } from '../../api/client';
import { fetchAssetTypes, fetchAssets } from '../../api/ops';
import { useSiteScope } from '../../app/scope';
import { deviceLive } from '../../realtime/stores';
import { formatNumber } from '../../ui/format';
import { useMe } from '../auth/session';
import { assetsData, ASSETS_SOURCE, heatData, HEAT_SOURCE, setOverlay } from './overlays';

/** Metrics offered for the heat map (sensor, traffic and electrical values the devices report). */
export const HEAT_METRICS = [
  'electrical.power_w',
  'light.dimmer_pct',
  'electrical.voltage_v',
  'environment.temperature_c',
  'environment.illuminance_lux',
  'environment.pm10_ugm3',
  'environment.pm25_ugm3',
  'environment.noise_db',
  'traffic.vehicle_count',
  'traffic.pedestrian_count',
  'motion.event_count',
];

/**
 * Map overlays (AFM f, g): GIS assets and a heat map of a metric. The heat map is built from the live store and
 * refreshed at most every 10 s while it is shown (throttled; realtime deltas never rebuild it per event).
 */
export function OverlayPanel({ map }: { map: MapLibreMap | null }) {
  const { t } = useTranslation();
  const me = useMe().data;
  const siteId = useSiteScope((s) => s.siteId);
  const [open, setOpen] = useState(false);
  const [showAssets, setShowAssets] = useState(false);
  const [metric, setMetric] = useState('');
  const [range, setRange] = useState<{ min: number; max: number; n: number } | null>(null);
  const canAssets = canAny(me, 'asset.view');
  const types = useQuery({
    queryKey: ['asset-types'],
    queryFn: fetchAssetTypes,
    enabled: canAssets && showAssets,
    staleTime: 600_000,
  });
  const assets = useQuery({
    queryKey: ['assets', 'map', siteId],
    queryFn: () => fetchAssets({ site_id: siteId }),
    enabled: canAssets && showAssets,
  });
  useEffect(() => {
    if (!map) return;
    setOverlay(
      map,
      ASSETS_SOURCE,
      showAssets && assets.data && types.data ? assetsData(assets.data.items, types.data.items) : null,
    );
  }, [map, showAssets, assets.data, types.data]);
  useEffect(() => {
    if (!map) return;
    if (!metric) {
      setOverlay(map, HEAT_SOURCE, null);
      setRange(null);
      return;
    }
    const draw = () => {
      const h = heatData(metric);
      setOverlay(map, HEAT_SOURCE, h.data);
      setRange({ min: h.min, max: h.max, n: h.n });
    };
    draw();
    let pending = false;
    const unsub = deviceLive.subscribeChanges(() => {
      if (pending) return;
      pending = true;
      setTimeout(() => {
        pending = false;
        draw();
      }, 10_000);
    });
    return unsub;
  }, [map, metric]);
  return (
    <>
      <button
        type="button"
        className="btn map-tool-btn"
        aria-pressed={open}
        onClick={() => setOpen((v) => !v)}
      >
        <Layers size={15} /> {t('mapOverlays.button')}
      </button>
      {open && (
        <div className="overlay-panel" role="group" aria-label={t('mapOverlays.button')}>
          {canAssets && (
            <label className="check-row">
              <input type="checkbox" checked={showAssets} onChange={(e) => setShowAssets(e.target.checked)} />
              {t('mapOverlays.assets')}
              {showAssets && assets.data && (
                <span className="muted small">({formatNumber(assets.data.total)})</span>
              )}
            </label>
          )}
          <label className="field">
            {t('mapOverlays.heat')}
            <select className="input" value={metric} onChange={(e) => setMetric(e.target.value)}>
              <option value="">{t('mapOverlays.none')}</option>
              {HEAT_METRICS.map((m) => (
                <option key={m} value={m}>
                  {t(`catalog.metric.${m}` as 'catalog.metric.electrical.power_w', { defaultValue: m })}
                </option>
              ))}
            </select>
          </label>
          {range && (
            <>
              <div className="heat-scale" aria-hidden />
              <div className="toolbar small muted">
                <span>{formatNumber(range.min, 1)}</span>
                <span style={{ flex: 1 }} />
                <span>{formatNumber(range.max, 1)}</span>
              </div>
              <p className="muted small" style={{ margin: 0 }}>
                {range.n ? t('mapOverlays.points', { n: range.n }) : t('mapOverlays.noData')}
              </p>
            </>
          )}
        </div>
      )}
    </>
  );
}
